// src/services/api/crewMemberAPI.js
import { createAuthHeaders } from '../../utils/authUtils';

const FIREBASE_FUNCTIONS_BASE_URL = process.env.REACT_APP_FIREBASE_FUNCTIONS_BASE_URL;

/**
 * Client-side API service for crew member operations
 * Calls Firebase Functions instead of direct database access
 */
class CrewMemberAPI {
  /**
   * Get all crew members
   * @returns {Promise<Array>} Array of crew members
   */
  static async getAll() {
    try {
      console.log('API: Getting all crew members');
      
      return await this._retryRequest(async () => {
        const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/crewMembers`);
        url.searchParams.set('operation', 'getAll');
        
        console.log('API: Making request to:', url.toString());
        
        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: await createAuthHeaders(),
        });
        
        if (!response.ok) {
          await this._handleAPIError(response, 'getAll');
        }
        
        const result = await response.json();
        
        if (!result.success) {
          throw new Error(result.error || 'API request was not successful');
        }
        
        console.log('API: Successfully retrieved all crew members', {
          count: result.data?.length || 0,
          activeCount: result.data?.filter(cm => cm.is_active).length || 0
        });
        
        return result.data || [];
      });
      
    } catch (error) {
      console.error('API Error in getAll crew members:', {
        error: error.message,
        stack: error.stack
      });
      
      throw new Error(`Failed to get crew members: ${error.message}`);
    }
  }

  /**
   * Get crew member by ID
   * @param {string} crewMemberId - Crew member ID
   * @returns {Promise<Object|null>} Crew member data or null if not found
   */
  static async getById(crewMemberId) {
    try {
      console.log('API: Getting crew member by ID', { crewMemberId });
      
      // Validate parameters
      if (!crewMemberId) {
        throw new Error('Crew member ID is required');
      }
      
      return await this._retryRequest(async () => {
        const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/crewMembers`);
        url.searchParams.set('operation', 'getById');
        url.searchParams.set('crewMemberId', crewMemberId);
        
        console.log('API: Making request to:', url.toString());
        
        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: await createAuthHeaders(),
        });
        
        if (!response.ok) {
          await this._handleAPIError(response, 'getById');
        }
        
        const result = await response.json();
        
        if (!result.success) {
          throw new Error(result.error || 'API request was not successful');
        }
        
        console.log('API: Successfully retrieved crew member by ID', {
          crewMemberId: result.data?.id,
          name: result.data?.name,
          found: !!result.data
        });
        
        return result.data;
      });
      
    } catch (error) {
      console.error('API Error in getById crew member:', {
        crewMemberId,
        error: error.message,
        stack: error.stack
      });
      
      throw new Error(`Failed to get crew member: ${error.message}`);
    }
  }

  /**
   * Create new crew member
   * @param {Object} crewMemberData - Crew member data
   * @returns {Promise<Object>} Created crew member
   */
  static async create(crewMemberData) {
    try {
      console.log('API: Creating new crew member', { crewMemberData });
      
      // Validate parameters
      if (!crewMemberData) {
        throw new Error('Crew member data is required');
      }
      
      if (!crewMemberData.name) {
        throw new Error('Name is required');
      }
      
      return await this._retryRequest(async () => {
        const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/crewMembers`);
        
        console.log('API: Making request to:', url.toString());
        
        const response = await fetch(url.toString(), {
          method: 'POST',
          headers: await createAuthHeaders(),
          body: JSON.stringify({
            operation: 'create',
            crewMemberData
          }),
        });
        
        if (!response.ok) {
          await this._handleAPIError(response, 'create');
        }
        
        const result = await response.json();
        
        if (!result.success) {
          throw new Error(result.error || 'API request was not successful');
        }
        
        console.log('API: Successfully created crew member', {
          crewMemberId: result.data.id,
          name: result.data.name,
          role: result.data.role
        });
        
        return result.data;
      });
      
    } catch (error) {
      console.error('API Error in create crew member:', {
        crewMemberData,
        error: error.message,
        stack: error.stack
      });
      
      throw new Error(`Failed to create crew member: ${error.message}`);
    }
  }

  /**
   * Update existing crew member
   * @param {string} crewMemberId - Crew member ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated crew member
   */
  static async update(crewMemberId, updates) {
    try {
      console.log('API: Updating crew member', { crewMemberId, updates });
      
      // Validate parameters
      if (!crewMemberId) {
        throw new Error('Crew member ID is required');
      }
      
      if (!updates || Object.keys(updates).length === 0) {
        throw new Error('Updates are required');
      }
      
      return await this._retryRequest(async () => {
        const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/crewMembers`);
        url.searchParams.set('crewMemberId', crewMemberId);
        
        console.log('API: Making request to:', url.toString());
        
        const response = await fetch(url.toString(), {
          method: 'POST',
          headers: await createAuthHeaders(),
          body: JSON.stringify({
            operation: 'update',
            updates
          }),
        });
        
        if (!response.ok) {
          await this._handleAPIError(response, 'update');
        }
        
        const result = await response.json();
        
        if (!result.success) {
          throw new Error(result.error || 'API request was not successful');
        }
        
        console.log('API: Successfully updated crew member', {
          crewMemberId: result.data.id,
          name: result.data.name,
          updatedFields: Object.keys(updates)
        });
        
        return result.data;
      });
      
    } catch (error) {
      console.error('API Error in update crew member:', {
        crewMemberId,
        updates,
        error: error.message,
        stack: error.stack
      });
      
      throw new Error(`Failed to update crew member: ${error.message}`);
    }
  }

  /**
   * Soft delete crew member (set is_active to false)
   * @param {string} crewMemberId - Crew member ID
   * @returns {Promise<Object>} Deactivated crew member
   */
  static async delete(crewMemberId) {
    try {
      console.log('API: Deleting crew member', { crewMemberId });
      
      // Validate parameters
      if (!crewMemberId) {
        throw new Error('Crew member ID is required');
      }
      
      return await this._retryRequest(async () => {
        const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/crewMembers`);
        url.searchParams.set('crewMemberId', crewMemberId);
        
        console.log('API: Making request to:', url.toString());
        
        const response = await fetch(url.toString(), {
          method: 'POST',
          headers: await createAuthHeaders(),
          body: JSON.stringify({
            operation: 'delete'
          }),
        });
        
        if (!response.ok) {
          await this._handleAPIError(response, 'delete');
        }
        
        const result = await response.json();
        
        if (!result.success) {
          throw new Error(result.error || 'API request was not successful');
        }
        
        console.log('API: Successfully deleted crew member', {
          crewMemberId: result.data.id,
          name: result.data.name,
          isActive: result.data.is_active
        });
        
        return result.data;
      });
      
    } catch (error) {
      console.error('API Error in delete crew member:', {
        crewMemberId,
        error: error.message,
        stack: error.stack
      });
      
      throw new Error(`Failed to delete crew member: ${error.message}`);
    }
  }

  /**
   * Retry mechanism for API requests with exponential backoff
   * @private
   */
  static async _retryRequest(requestFn, maxRetries = 2) {
    let lastError;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await requestFn();
      } catch (error) {
        lastError = error;
        
        // Don't retry on validation errors or client errors (4xx)
        if (error.message.includes('required') || 
            error.message.includes('Invalid') ||
            error.message.includes('already exists')) {
          throw error;
        }
        
        // Don't retry on last attempt
        if (attempt === maxRetries) {
          break;
        }
        
        // Calculate exponential backoff delay
        const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
        
        console.warn(`API request failed, retrying in ${delay}ms (attempt ${attempt}/${maxRetries})`, {
          error: error.message,
          attempt
        });
        
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    throw lastError;
  }

  /**
   * Handle API error responses
   * @private
   */
  static async _handleAPIError(response, operation) {
    let errorDetails;
    
    try {
      const errorData = await response.json();
      errorDetails = errorData.error || `HTTP ${response.status}`;
    } catch (parseError) {
      errorDetails = `HTTP ${response.status} ${response.statusText}`;
    }
    
    console.error(`API Error in ${operation}:`, {
      status: response.status,
      statusText: response.statusText,
      error: errorDetails
    });
    
    // Provide specific error messages based on status codes
    switch (response.status) {
      case 400:
        throw new Error(errorDetails || 'Invalid request data');
      case 404:
        throw new Error('Crew member not found');
      case 409:
        throw new Error(errorDetails || 'Crew member already exists');
      case 500:
        throw new Error('Internal server error. Please try again.');
      case 503:
        throw new Error('Service temporarily unavailable. Please try again later.');
      default:
        throw new Error(errorDetails || `Request failed with status ${response.status}`);
    }
  }
}

export { CrewMemberAPI };